import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Box,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Textarea,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import { IntakeFormAnswers } from "~/../../sdk/consultations";
import { SelectInput, CheckboxGroup } from "~/components";
import { Checkbox } from "~/OnboardingApp/components";

export interface MensFertilityFormValues {
  trying_to_conceive: string;
  current_symptoms: string[];
  fathered_children: string;
  medical_history: string[];
  additional_questions: string;
  lab_test_media_ids: string[];
  consent: boolean;
}

interface MensFertilityIntakeFormProps {
  labTestMediaIds: string[];
}

interface MensFertilityIntakeFormComponent
  extends FC<MensFertilityIntakeFormProps> {
  getInitialValues: () => MensFertilityFormValues;
  getValidationSchema: (t: any) => any;
  buildIntakeAnswers: (
    values: MensFertilityFormValues,
    labTestMediaIds: string[],
  ) => IntakeFormAnswers;
}

export const MensFertilityIntakeForm: MensFertilityIntakeFormComponent = () => {
  const { t } = useTranslation();
  const formik = useFormikContext<MensFertilityFormValues>();

  return (
    <VStack w="full" alignItems="flex-start" spacing={6}>
      {/* Question 1: How long trying to conceive */}
      <SelectInput
        id="trying_to_conceive"
        label={t(
          "diagnosticDashboard.intake.questions.mensTryingToConceive.label",
        )}
        placeholder={t(
          "diagnosticDashboard.intake.questions.mensTryingToConceive.placeholder",
        )}
        options={[
          {
            value: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.notStartedCurious",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.notStartedCurious",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.notStartedSoon",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.notStartedSoon",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.oneToSix",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.oneToSix",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.sixToTwelve",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.sixToTwelve",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.moreThanYear",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.moreThanYear",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.notTrying",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.mensTryingToConceive.options.notTrying",
            ),
          },
        ]}
      />

      {/* Question 2: Fathered children */}
      <SelectInput
        id="fathered_children"
        label={t("diagnosticDashboard.intake.questions.fatheredChildren.label")}
        placeholder={t(
          "diagnosticDashboard.intake.questions.fatheredChildren.placeholder",
        )}
        options={[
          {
            value: t(
              "diagnosticDashboard.intake.questions.fatheredChildren.options.no",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.fatheredChildren.options.no",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.fatheredChildren.options.yes",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.fatheredChildren.options.yes",
            ),
          },
        ]}
      />

      {/* Question 3: Current symptoms */}
      <CheckboxGroup
        name="current_symptoms"
        label={t("diagnosticDashboard.intake.questions.currentSymptoms.label")}
        options={[
          {
            value: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.fatigue",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.fatigue",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.weightGain",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.weightGain",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.decreasedLibido",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.decreasedLibido",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.erectileDysfunction",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.erectileDysfunction",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.depression",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.depression",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.none",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.currentSymptoms.options.none",
            ),
          },
        ]}
      />

      {/* Question 4: Medical history */}
      <CheckboxGroup
        name="medical_history"
        label={t("diagnosticDashboard.intake.questions.medicalHistory.label")}
        options={[
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.smoking",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.smoking",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.marijuana",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.marijuana",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.opioids",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.opioids",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.testicularInfection",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.testicularInfection",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.sti",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.sti",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.undescendedTesticles",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.undescendedTesticles",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.testicularTrauma",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.testicularTrauma",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.radiation",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.radiation",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.heatExposure",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.heatExposure",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.testosterone",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.testosterone",
            ),
          },
          {
            value: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.none",
            ),
            label: t(
              "diagnosticDashboard.intake.questions.medicalHistory.options.none",
            ),
          },
        ]}
      />

      {/* Question 5: Additional questions */}
      <FormControl
        isInvalid={
          !!(
            formik.errors.additional_questions &&
            formik.touched.additional_questions
          )
        }
      >
        <FormLabel htmlFor="additional_questions">
          {t("diagnosticDashboard.intake.questions.additionalQuestions.label")}
        </FormLabel>
        <Textarea
          id="additional_questions"
          placeholder={t(
            "diagnosticDashboard.intake.questions.additionalQuestions.placeholder",
          )}
          {...formik.getFieldProps("additional_questions")}
          rows={4}
        />
        <FormErrorMessage>
          {formik.errors.additional_questions}
        </FormErrorMessage>
      </FormControl>

      {/* Question 6: Consent */}
      <Box width="100%">
        <Text fontWeight="500" mb={2}>
          {t("diagnosticDashboard.intake.questions.consent.label")}
        </Text>
        <Box p={4} bg="gray.50" borderRadius="md" mb={3} fontSize="sm">
          <Text mb={2}>
            {t("diagnosticDashboard.intake.questions.consent.disclaimer")}
          </Text>
          <Text>{t("diagnosticDashboard.intake.questions.consent.terms")}</Text>
        </Box>
        <Checkbox id="consent" name="consent">
          {t("diagnosticDashboard.intake.questions.consent.checkbox")}
        </Checkbox>
      </Box>
    </VStack>
  );
};

MensFertilityIntakeForm.getInitialValues = (): MensFertilityFormValues => ({
  trying_to_conceive: "",
  current_symptoms: [],
  fathered_children: "",
  medical_history: [],
  additional_questions: "",
  lab_test_media_ids: [],
  consent: false,
});

MensFertilityIntakeForm.getValidationSchema = (t: any) =>
  Yup.object({
    trying_to_conceive: Yup.string().required(
      t("diagnosticDashboard.intake.validation.tryingToConceiveRequired"),
    ),
    current_symptoms: Yup.array()
      .of(Yup.string())
      .min(1, t("diagnosticDashboard.intake.validation.currentSymptomsRequired"))
      .required(
        t("diagnosticDashboard.intake.validation.currentSymptomsRequired"),
      ),
    fathered_children: Yup.string().required(
      t("diagnosticDashboard.intake.validation.fatheredChildrenRequired"),
    ),
    medical_history: Yup.array()
      .of(Yup.string())
      .min(1, t("diagnosticDashboard.intake.validation.medicalHistoryRequired"))
      .required(
        t("diagnosticDashboard.intake.validation.medicalHistoryRequired"),
      ),
    additional_questions: Yup.string(),
    consent: Yup.boolean().oneOf(
      [true],
      t("diagnosticDashboard.intake.validation.consentRequired"),
    ),
  });

MensFertilityIntakeForm.buildIntakeAnswers = (
  values: MensFertilityFormValues,
  labTestMediaIds: string[],
): IntakeFormAnswers => ({
  trying_to_conceive: {
    fh_id: "ques_evaluation_only_male_fertility_intake_01",
    value: values.trying_to_conceive,
    stepId: "intake",
    type: "text" as const,
  },
  current_symptoms: {
    fh_id: "ques_evaluation_only_male_fertility_intake_02",
    value: values.current_symptoms,
    stepId: "intake",
    type: "options" as const,
  },
  fathered_children: {
    fh_id: "ques_evaluation_only_male_fertility_intake_03",
    value: values.fathered_children,
    stepId: "intake",
    type: "text" as const,
  },
  medical_history: {
    fh_id: "ques_evaluation_only_male_fertility_intake_04",
    value: values.medical_history,
    stepId: "intake",
    type: "options" as const,
  },
  additional_questions: {
    fh_id: "ques_evaluation_only_male_fertility_intake_05",
    value: values.additional_questions,
    stepId: "intake",
    type: "text" as const,
  },
  consent: {
    fh_id: "ques_evaluation_only_male_fertility_intake_06",
    value: values.consent
      ? "I have read the above information and I do consent and wish to move forward"
      : "",
    stepId: "intake",
    type: "text" as const,
  },
  lab_test_media_ids: {
    fh_id: "ques_evaluation_only_male_fertility_intake_07",
    value: labTestMediaIds,
    stepId: "intake",
    type: "media" as const,
  },
});
